import type {
  DirectCallSpeech,
  DirectCallSpeechAdapter,
} from './direct-call-playback';
import { DirectCallPcmDecoder } from './direct-call-pcm';
import { DirectCallPlaybackInterrupted } from './direct-call-playback';

export type DirectCallAudioEvent =
  | { type: 'pcm'; bytes: Uint8Array; played?: () => void }
  | { type: 'completed' };

// The authenticated transport validates conversation/turn attribution and format.
// Aborting must unblock pending reads; EOF alone is not synthesis success.
export interface DirectCallAudioSource {
  stream(
    reply: DirectCallSpeech,
    signal: AbortSignal
  ): AsyncIterable<DirectCallAudioEvent>;
}

export interface DirectCallPcmOutput {
  readonly interrupted?: AbortSignal;
  readonly capacity?: number;
  // Resolve only after these samples have played, not merely entered a buffer.
  write(samples: Float32Array, signal: AbortSignal): Promise<void>;
  finish(signal: AbortSignal): Promise<void>;
  // Idempotently stop audible output and settle pending write/finish operations.
  stop(): Promise<void>;
}

export interface DirectCallPcmSink {
  open(sampleRate: 16000, signal: AbortSignal): Promise<DirectCallPcmOutput>;
}

// Pre-roll (Bug 34524): hold ~1.5 s of speech before the first native write so a
// network or synthesis gap between chunks drains this cushion instead of going
// silent mid-sentence. Completion flushes early, so a short reply never waits.
export const DIRECT_CALL_PREROLL_SAMPLES = 24000;
// Never hold more chunks than stay inside the speech source's unacknowledged
// window (seq <= played + 4), or small early chunks could stall the stream.
export const DIRECT_CALL_PREROLL_MAX_CHUNKS = 3;

export class DirectCallStreamingSpeechAdapter implements DirectCallSpeechAdapter {
  constructor(
    private readonly source: DirectCallAudioSource,
    private readonly sink: DirectCallPcmSink
  ) {}

  async speak(reply: DirectCallSpeech, signal: AbortSignal): Promise<void> {
    const operation = new AbortController();
    const decoder = new DirectCallPcmDecoder();
    let output: DirectCallPcmOutput | undefined;
    let stopping: Promise<void> | undefined;
    const pending = new Set<Promise<void>>();
    let orderedPlayed = Promise.resolve();
    let partialSample = false;
    let playbackError: unknown;
    const stop = (): Promise<void> => {
      if (!output) return Promise.resolve();
      stopping ??= Promise.resolve().then(() => output!.stop());
      // Abort listeners cannot await, but finally observes the same rejection.
      void stopping.catch(() => undefined);
      return stopping;
    };
    const abort = (): void => {
      operation.abort();
      void stop();
    };
    const check = (): void => {
      if (playbackError) throw playbackError;
      if (operation.signal.aborted) throw new Error('Audio playback cancelled');
    };
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
    try {
      check();
      output = await this.sink.open(16000, operation.signal);
      output.interrupted?.addEventListener('abort', abort, { once: true });
      if (output.interrupted?.aborted) abort();
      check();
      const capacity = Number.isSafeInteger(output.capacity)
        ? Math.min(4, Math.max(1, output.capacity!))
        : 1;
      let completed = false;
      let prerolling = true;
      let prerollSamples = 0;
      const preroll: { samples: Float32Array; played?: () => void }[] = [];
      const enqueue = async (
        samples: Float32Array,
        acknowledge?: () => void
      ): Promise<void> => {
        const played = samples.length
          ? output!.write(samples, operation.signal)
          : Promise.resolve();
        // Schedule native writes immediately; only their consumed callbacks are
        // serialized, including empty chunks that have no native completion.
        const task = Promise.all([orderedPlayed, played])
          .then(() => {
            check();
            acknowledge?.();
          })
          .catch((error) => {
            playbackError = error;
            abort();
          })
          .finally(() => pending.delete(task));
        orderedPlayed = task;
        pending.add(task);
        if (pending.size >= capacity) {
          await Promise.race(pending);
          check();
        }
      };
      const flush = async (): Promise<void> => {
        prerolling = false;
        for (const held of preroll.splice(0))
          await enqueue(held.samples, held.played);
      };
      for await (const event of this.source.stream(reply, operation.signal)) {
        check();
        if (completed) throw new Error('Audio data after completion');
        if (event.type === 'completed') {
          decoder.finish();
          completed = true;
          await flush();
        } else if (event.type === 'pcm') {
          // Bound each decoded allocation to two seconds of 16-bit mono PCM.
          if (event.bytes.byteLength > 64000)
            throw new Error('Audio chunk exceeds playback limit');
          // The acknowledged speech source requires whole samples. Keep decoder
          // carry for other sources, but never acknowledge an unplayed half-sample.
          if (event.played && (partialSample || event.bytes.byteLength % 2))
            throw new Error('Acknowledged PCM chunks require complete samples');
          partialSample =
            (Number(partialSample) + event.bytes.byteLength) % 2 !== 0;
          const samples = decoder.decode(event.bytes);
          if (prerolling) {
            preroll.push({ samples, played: event.played });
            prerollSamples += samples.length;
            if (
              prerollSamples >= DIRECT_CALL_PREROLL_SAMPLES ||
              preroll.length >= DIRECT_CALL_PREROLL_MAX_CHUNKS
            )
              await flush();
          } else {
            await enqueue(samples, event.played);
          }
        } else {
          throw new Error('Unsupported audio event');
        }
      }
      check();
      if (!completed) throw new Error('Audio stream ended without completion');
      await Promise.all(pending);
      check();
      await output.finish(operation.signal);
      check();
    } catch (error) {
      if (output?.interrupted?.aborted)
        throw new DirectCallPlaybackInterrupted();
      throw error;
    } finally {
      signal.removeEventListener('abort', abort);
      output?.interrupted?.removeEventListener('abort', abort);
      operation.abort();
      decoder.cancel();
      await stop();
      await Promise.all(pending);
    }
  }
}
